// Vendor Libs
import React, {useEffect, useState, useContext} from 'react';
import PropTypes from 'prop-types';

// Material UI
import {Button, Container, IconButton, InputAdornment, Tooltip} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import {makeStyles} from '@material-ui/core/styles';
import RemoveRedEye from '@material-ui/icons/RemoveRedEye';

// Material Design Icons
import EyeOff from 'mdi-material-ui/EyeOff';

// Dish Tech React
import {InputTextField} from '@dishtech-react/core';
import {useForm, useToast, useRouter} from '@dishtech-react/hooks';

// Contexts
import {RouteContext} from '../../contexts/route-context';

// Images
import logo from './ripple-logo.svg';

// Config
import {serviceConfig} from '../../../config/service-config';

const FORM_KEY = 'RESET_PASSWORD';
const MIN_USER_NAME_LEN = 4;
const MIN_CREDENTIAL_LEN = 8;

const useStyles = makeStyles(theme => ({
    card: {
        margin: 'auto',
        marginTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    eye: {
        cursor: 'pointer',
        color: '#3f51b5',
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));

const ResetPasswordPage = ({redirectUrl}) => {
    const {routerParams} = useRouter();
    const classes = useStyles();
    const routeContext = useContext(RouteContext);
    const {
        formValues: {credential = '', credentialConfirm = '', username = ''},
        registerForm,
        resetForm,
        setFieldValue,
        submitForm,
    } = useForm(serviceConfig, FORM_KEY, 'UPDATE');
    const {createToast} = useToast();
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [masked, setMasked] = useState(true);

    useEffect(() => {
        registerForm();

        return () => resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = () => {
        setIsButtonDisabled(true);
        if (
            credentialConfirm.length < MIN_CREDENTIAL_LEN ||
            credential.length < MIN_CREDENTIAL_LEN ||
            username.length < MIN_USER_NAME_LEN
        ) {
            const msg =
                username.length < MIN_USER_NAME_LEN
                    ? 'Username must be at least 4 characters'
                    : 'Password must be at least 8 characters';

            createToast(msg, {
                variant: 'error',
                persist: true,
                onClose: () => setIsButtonDisabled(false),
            });
        } else if (credentialConfirm !== credential) {
            createToast('Passwords do not match', {
                variant: 'error',
                persist: true,
                onClose: () => setIsButtonDisabled(false),
            });
        } else {
            const queryParams = {
                userName: username,
                token: routerParams,
                password: credential,
            };

            submitForm(queryParams).then(({error}) => {
                const msg = error || 'Your password has been successfully reset.';

                createToast(msg, {
                    variant: error ? 'error' : 'success',
                    persist: true,
                    onClose: error ? () => setIsButtonDisabled(false) : () => routeContext.push(redirectUrl),
                });
            });
        }
    };

    const passwordInputField = (fieldValue, fieldLabel, fieldName) => {
        const [action, inputType, Icon] = masked ? ['Show', 'password', RemoveRedEye] : ['Hide', 'text', EyeOff];

        return (
            <InputTextField
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={`${action} password`}>
                                <IconButton className={classes.eye} onClick={() => setMasked(!masked)}>
                                    <Icon />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
                autoComplete="current-password"
                label={fieldLabel}
                type={inputType}
                value={fieldValue}
                onChange={event => setFieldValue(fieldName, event.target.value)}
            />
        );
    };

    return (
        <Container maxWidth="xs">
            <div className={classes.card}>
                <img alt="ripple logo" src={logo} />

                <InputTextField
                    autoComplete="username"
                    autoFocus={true}
                    id="Username"
                    label="Username"
                    name="username"
                    type="username"
                    value={username}
                    onChange={event => setFieldValue('username', event.target.value)}
                />
                {passwordInputField(credential, 'New Password', 'credential')}
                {passwordInputField(credentialConfirm, 'Retype New Password', 'credentialConfirm')}

                <Button
                    className={classes.submit}
                    color="primary"
                    disabled={
                        username.length === 0 ||
                        credential.length === 0 ||
                        credentialConfirm.length === 0 ||
                        isButtonDisabled
                    }
                    fullWidth={true}
                    startIcon={<SaveIcon />}
                    variant="contained"
                    onClick={handleSubmit}
                >
                    Submit
                </Button>
            </div>
        </Container>
    );
};

const ResetPassword = ({path, redirectUrl}) => (
    <div className="ResetPassword">
        <RouteContext.Provider value={useRouter()}>
            <ResetPasswordPage path={path} redirectUrl={redirectUrl} />
        </RouteContext.Provider>
    </div>
);

ResetPassword.propTypes = {
    path: PropTypes.string,
    redirectUrl: PropTypes.string,
};

ResetPasswordPage.propTypes = {
    redirectUrl: PropTypes.string,
};

export default ResetPassword;
